import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, MessageCircle, Mail, Phone, MapPin } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettings';
import { mediaUrl } from '@/admin/api';

export const Footer = () => {
  const { settings } = useSiteSettings();
  const currentYear = new Date().getFullYear();
  const copyright = settings.footer_copyright || `© ${currentYear} ${settings.site_name}. All rights reserved.`;

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              {settings.logo_url ? (
                <img src={mediaUrl(settings.logo_url)} alt={settings.site_name} className="w-10 h-10 rounded-full object-cover bg-white" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                  <span className="text-white font-bold font-serif">MT</span>
                </div>
              )}
              <span className="text-lg font-serif font-bold">{settings.site_name}</span>
            </div>
            <p className="text-sm text-secondary-foreground/80 leading-relaxed">
              {settings.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">About Us</Link></li>
              <li><Link to="/membership" className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">Membership</Link></li>
              <li><Link to="/events" className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">Events</Link></li>
              <li><Link to="/gallery" className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors">Gallery</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              {settings.contact_email && (
                <li className="flex items-start space-x-2 text-sm text-secondary-foreground/80">
                  <Mail size={18} className="mt-0.5 flex-shrink-0" />
                  <span data-testid="footer-email">{settings.contact_email}</span>
                </li>
              )}
              {settings.contact_phone && (
                <li className="flex items-start space-x-2 text-sm text-secondary-foreground/80">
                  <Phone size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{settings.contact_phone}</span>
                </li>
              )}
              {settings.address && (
                <li className="flex items-start space-x-2 text-sm text-secondary-foreground/80">
                  <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{settings.address}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-4">Connect With Us</h3>
            <div className="flex space-x-3">
              {settings.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-secondary-foreground/10 hover:bg-accent flex items-center justify-center transition-all duration-300 hover:scale-110" aria-label="Facebook">
                  <Facebook size={18} />
                </a>
              )}
              {settings.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-secondary-foreground/10 hover:bg-accent flex items-center justify-center transition-all duration-300 hover:scale-110" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
              )}
              {settings.twitter_url && (
                <a href={settings.twitter_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-secondary-foreground/10 hover:bg-accent flex items-center justify-center transition-all duration-300 hover:scale-110" aria-label="Twitter">
                  <Twitter size={18} />
                </a>
              )}
              {settings.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-secondary-foreground/10 hover:bg-accent flex items-center justify-center transition-all duration-300 hover:scale-110" aria-label="WhatsApp">
                  <MessageCircle size={18} />
                </a>
              )}
            </div>
            {settings.office_hours && (
              <p className="mt-6 text-sm text-secondary-foreground/80">{settings.office_hours}</p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-secondary-foreground/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-secondary-foreground/70" data-testid="footer-copyright">{copyright}</p>
            <Link to="/admin/login" className="text-xs text-secondary-foreground/50 hover:text-accent transition-colors">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
